import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Pencil, Trash2, Plus, LogOut } from "lucide-react";
import { adminApi, getAdminToken, setAdminToken } from "./AdminLogin";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { title: "", code: "", description: "", sector: "", status: "Completato", value: "", year: "" };

const inputCls =
  "w-full bg-white border border-slate-300 px-4 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-blue-500 placeholder:text-slate-400";

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [checking, setChecking] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await adminApi().get("/admin/cantieri");
    setItems(data);
  };

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin/login");
      return;
    }
    adminApi()
      .get("/auth/me")
      .then(load)
      .catch(() => {
        setAdminToken(null);
        navigate("/admin/login");
      })
      .finally(() => setChecking(false));
  }, [navigate]);

  const resetForm = () => {
    setForm(EMPTY);
    setFiles([]);
    setExistingImages([]);
    setEditingId(null);
    const input = document.getElementById("cantiere-file");
    if (input) input.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      if (editingId) {
        await adminApi().put(`/admin/cantieri/${editingId}`, fd);
        toast.success("Cantiere aggiornato");
      } else {
        await adminApi().post("/admin/cantieri", fd);
        toast.success("Cantiere pubblicato sul sito");
      }
      resetForm();
      await load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  const edit = (c) => {
    setEditingId(c.id);
    setForm({ title: c.title, code: c.code, description: c.description, sector: c.sector, status: c.status, value: c.value_eur != null ? String(c.value_eur) : "", year: c.year != null ? String(c.year) : "" });
    setFiles([]);
    setExistingImages(c.images || []);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeFoto = async (url) => {
    try {
      await adminApi().delete(`/admin/cantieri/${editingId}/foto`, { params: { image_url: url } });
      setExistingImages((prev) => prev.filter((u) => u !== url));
      toast.success("Foto rimossa");
      await load();
    } catch {
      toast.error("Rimozione non riuscita");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare "${c.title}"?`)) return;
    try {
      await adminApi().delete(`/admin/cantieri/${c.id}`);
      toast.success("Cantiere eliminato");
      await load();
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const logout = () => {
    setAdminToken(null);
    navigate("/admin/login");
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/assets/logo.png" alt="Saircos" className="h-10 w-auto" />
            <span className="text-xs font-mono uppercase tracking-widest text-slate-500">Area Riservata · Cantieri</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-xs font-mono text-slate-500 hover:text-blue-600 transition-colors">Vedi sito →</a>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-700 hover:text-red-600 transition-colors">
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-5 gap-10">
        <section className="lg:col-span-2">
          <div className="bg-white border border-slate-200 p-6">
            <h2 className="text-lg font-extrabold uppercase text-slate-950 flex items-center gap-2">
              <Plus size={18} className="text-blue-600" />
              {editingId ? "Modifica cantiere" : "Nuovo cantiere"}
            </h2>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="cantiere-form">
              <div>
                <label className="block text-xs font-mono uppercase text-slate-500 mb-1.5">Titolo intervento *</label>
                <input data-testid="cantiere-input-title" required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es. Rifacimento facciata Condominio Via Verdi 8" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-mono uppercase text-slate-500 mb-1.5">Codice commessa</label>
                  <input data-testid="cantiere-input-code" className={inputCls} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Es. 01" />
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase text-slate-500 mb-1.5">Settore</label>
                  <input data-testid="cantiere-input-sector" className={inputCls} value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} placeholder="Condomini / Privati / PA" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase text-slate-500 mb-1.5">Stato</label>
                <select data-testid="cantiere-input-status" className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option>Completato</option>
                  <option>In corso</option>
                  <option>In consegna</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase text-slate-500 mb-1.5">Importo lavori (€)</label>
                <input data-testid="cantiere-input-value" className={inputCls} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="Es. 141.027,52" />
                <p className="mt-1 text-[10px] font-mono text-slate-400">Viene sommato al "Valore opere realizzate" in home</p>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase text-slate-500 mb-1.5">Anno lavori</label>
                <input data-testid="cantiere-input-year" type="number" min="2019" max="2100" className={inputCls} value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="Es. 2024" />
                <p className="mt-1 text-[10px] font-mono text-slate-400">Alimenta il grafico di crescita in home</p>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase text-slate-500 mb-1.5">Descrizione</label>
                <textarea data-testid="cantiere-input-description" rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Lavori eseguiti, materiali, tempistiche..." />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase text-slate-500 mb-1.5">
                  Foto cantiere {editingId ? "(aggiungi altre foto)" : "(puoi caricarne più di una)"}
                </label>
                <input id="cantiere-file" data-testid="cantiere-input-file" type="file" multiple accept="image/jpeg,image/png,image/webp" className="w-full text-xs font-mono text-slate-600 file:mr-3 file:px-4 file:py-2 file:bg-slate-900 file:text-white file:border-0 file:text-xs file:font-bold file:uppercase file:cursor-pointer" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                {files.length > 0 && (
                  <p className="mt-1.5 text-[11px] font-mono text-slate-500">{files.length} nuova/e foto selezionata/e</p>
                )}
              </div>
              {existingImages.length > 0 && (
                <div>
                  <label className="block text-xs font-mono uppercase text-slate-500 mb-1.5">Foto pubblicate ({existingImages.length})</label>
                  <div className="grid grid-cols-4 gap-2" data-testid="cantiere-existing-photos">
                    {existingImages.map((u) => (
                      <div key={u} className="relative group">
                        <img src={`${BACKEND}${u}`} alt="Foto cantiere" className="w-full h-16 object-cover border border-slate-200" />
                        <button type="button" onClick={() => removeFoto(u)} data-testid="cantiere-photo-remove" className="absolute top-1 right-1 bg-red-600 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Rimuovi foto">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} data-testid="cantiere-submit-button" className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-widest text-xs transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                  {saving && <Loader2 className="animate-spin" size={14} />}
                  {saving ? "Salvataggio..." : editingId ? "Salva modifiche" : "Pubblica cantiere"}
                </button>
                {editingId && (
                  <button type="button" onClick={resetForm} data-testid="cantiere-cancel-button" className="px-6 py-3 bg-slate-100 border border-slate-300 text-slate-700 font-bold uppercase tracking-widest text-xs hover:bg-slate-200 transition-colors">
                    Annulla
                  </button>
                )}
              </div>
            </form>
          </div>
        </section>

        <section className="lg:col-span-3">
          <h2 className="text-lg font-extrabold uppercase text-slate-950 mb-6">
            Cantieri pubblicati <span className="text-blue-600 font-mono">({items.length})</span>
          </h2>
          {items.length === 0 ? (
            <div data-testid="cantieri-empty" className="bg-white border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500 font-mono">
              Nessun cantiere pubblicato. Sul sito compaiono le schede dimostrative.
            </div>
          ) : (
            <div className="space-y-4" data-testid="cantieri-list">
              {items.map((c) => (
                <div key={c.id} data-testid={`cantiere-row-${c.id}`} className="bg-white border border-slate-200 p-4 flex gap-4 items-center">
                  {c.image_url ? (
                    <img src={`${BACKEND}${c.image_url}`} alt={c.title} className="w-24 h-16 object-cover border border-slate-200 shrink-0" />
                  ) : (
                    <div className="w-24 h-16 border border-slate-200 bg-slate-100 shrink-0 flex items-center justify-center text-[9px] font-mono text-slate-400 uppercase">No foto</div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-slate-900 text-sm truncate">{c.title}</p>
                    <p className="text-xs font-mono text-slate-500 mt-0.5">
                      {[c.code && `N. ${c.code}`, c.sector, c.status].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <button onClick={() => edit(c)} data-testid={`cantiere-edit-${c.id}`} className="p-2 text-slate-500 hover:text-blue-600 transition-colors" aria-label="Modifica">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => remove(c)} data-testid={`cantiere-delete-${c.id}`} className="p-2 text-slate-500 hover:text-red-600 transition-colors" aria-label="Elimina">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
